import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { log } from '../utils/errors/logger';
import entraAuthService from '../services/EntraAuthService';
import AuthService from '../services/AuthService';

interface AuthCallbackProps {
  onLogin: (user: any) => void;  // ✅ CHANGED: Removed token parameter (cookies handle auth)
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleCallback = async () => {
      log.info('AuthCallback: Starting callback processing...');
      log.debug('URL details', {
        searchParams: Object.fromEntries(searchParams.entries()),
        hash: window.location.hash,
        fullUrl: window.location.href
      });
      
      try {
        // For MSAL, we don't need to manually parse parameters
        // MSAL handles the callback automatically when we call handleRedirectPromise()
        log.info('MSAL callback detected, processing authentication...');
        
        // Initialize MSAL if not already initialized
        await entraAuthService.initialize();
        
        // Handle the MSAL redirect and get tokens
        const result = await entraAuthService.handleRedirectPromise();
        
        if (!result) {
          log.error('No result from MSAL handleRedirectResult');
          setError(t('auth.callback.authFailed'));
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        log.info('MSAL authentication successful', {
          userId: result.user.id,
          username: result.user.username,
          email: result.user.email
        });

        // ✅ CHANGED: Backend should set HttpOnly cookies during Entra auth flow
        // The result from entraAuthService should already have cookies set
        // We just need to cache the user data locally for UI

        // Cache user data locally (for UI display only, NOT authentication)
        AuthService.setUser(result.user);

        // Call the onLogin handler (now without token parameter)
        if (onLogin) {
          onLogin(result.user);
        }
        

        // The onLogin should handle navigation to dashboard
      } catch (error) {
        log.error('Error processing auth callback', { error });
        setError(t('auth.callback.processFailed'));
        
        // Redirect to login
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, onLogin, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(0, 48, 100, 0.04) 0%, rgba(255, 255, 255, 0.85) 30%)',
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('auth.callback.redirecting')}
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('auth.callback.completing')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auth.callback.pleaseWait')}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback;